import React, { useState, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Modal, ScrollView, Platform, Alert } from 'react-native';
import { X, RotateCcw, Info } from 'lucide-react-native';
import { useAuth } from '@/hooks/useAuth';
import { REVENUECAT_CONFIG } from '@/utils/revenueCatConfig';
import { palette, spacing, radius, typography } from '@/styles/tokens';
import { useSubscription } from '@/hooks/useSubscription';
import { useTranslation } from '@/hooks/useTranslation';
import { useTheme } from '@/hooks/useTheme';
import { useCampaignConfig } from '@/hooks/useCampaignConfig';
import { router } from 'expo-router';
import {
  trackPaywallOpen,
  trackPaywallClose,
  trackPaywallProductClick,
  trackPaywallLearnMore,
  trackRestorePurchases,
  PaywallSource,
} from '@/src/analytics';
import { PremiumSubscriptionCard } from '@/components/PremiumSubscriptionCard';

interface CustomPaywallProps {
  visible: boolean;
  onClose: () => void;
  onPurchaseSuccess?: () => void;
  source?: PaywallSource;
  contextMessage?: string;
}

export const CustomPaywall: React.FC<CustomPaywallProps> = ({ visible, onClose, onPurchaseSuccess, source = 'profile', contextMessage }) => {
  const { colors: { primary: primaryColor } } = useTheme();
  const { t } = useTranslation();
  const { authState } = useAuth();
  const { products, isPurchasing, isIAPReady, purchaseSubscription, restorePurchases } = useSubscription();
  const { campaignCountriesLanguagesId } = useCampaignConfig();
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const hasTrackedOpenRef = useRef(false);

  useEffect(() => {
    if (visible && !hasTrackedOpenRef.current) {
      trackPaywallOpen(source);
      hasTrackedOpenRef.current = true;
    }
    if (!visible) {
      hasTrackedOpenRef.current = false;
    }
  }, [visible, source]);

  const handleClose = (dismissMethod: 'button' | 'background_tap' = 'button') => {
    trackPaywallClose(source, dismissMethod);
    onClose();
  };

  const handlePurchase = async (productId: string) => {
    if (isPurchasing || !isIAPReady) {
      return;
    }

    const product = products.find(p => p.productId === productId);
    trackPaywallProductClick({
      productId,
      productTitle: product?.title,
      localizedPrice: product?.localizedPrice,
    });

    try {
      await purchaseSubscription(productId);
      if (onPurchaseSuccess) {
        onPurchaseSuccess();
      } else {
        trackPaywallClose(source, 'purchase_complete');
        onClose();
      }
    } catch (error: any) {
      if (error?.userCancelled) {
        handleClose('button');
      } else {
        console.error('Purchase error in paywall:', error);
        handleClose('button');
      }
    }
  };

  const handleRestore = async () => {
    if (isPurchasing) {
      return;
    }

    trackRestorePurchases('initiated');
    try {
      await restorePurchases();
      trackRestorePurchases('success');
      if (onPurchaseSuccess) {
        onPurchaseSuccess();
      } else {
        trackPaywallClose(source, 'purchase_complete');
        onClose();
      }
    } catch (error: any) {
      console.error('Restore error in paywall:', error);
      trackRestorePurchases('failed', undefined, error?.message || 'Restore failed');
    }
  };

  const handleLearnMore = () => {
    trackPaywallLearnMore();
    setShowDetailsModal(true);
  };

  const handleOpenPricingConditions = () => {
    onClose();
    router.push('/legal-doc?slug=conditions-tarifaires');
  };

  const handleOpenTerms = () => {
    onClose();
    router.push('/legal-doc?slug=conditions-generales-dutilisation-ios');
  };

  const handleOpenPrivacy = () => {
    onClose();
    router.push('/legal-doc?slug=politique-de-confidentialite');
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={() => handleClose('button')}
    >
      <View style={styles.overlay}>
        <View style={styles.container}>
          <View style={styles.header}>
            <View style={{ flex: 1 }} />
            <TouchableOpacity onPress={() => handleClose('button')} style={styles.closeButton}>
              <X size={22} color="#ffffff" />
            </TouchableOpacity>
          </View>

          {contextMessage && (
            <View style={[styles.contextBanner, { backgroundColor: primaryColor }]}>
              <Text style={styles.contextBannerText}>{contextMessage}</Text>
            </View>
          )}

          <ScrollView
            style={styles.content}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollViewContent}
          >
            <PremiumSubscriptionCard
              onSubscribe={async (method, productId) => {
                console.log('[CustomPaywall] onSubscribe triggered', {
                  method,
                  productId,
                  isIAPReady,
                  isPurchasing,
                  productsCount: products.length,
                  firstProductId: products[0]?.productId,
                });

                if (method === 'dcb') {
                  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
                  try {
                    const origin = window.location.origin;
                    const supabaseUrl = process.env.EXPO_PUBLIC_SUPABASE_URL;
                    const supabaseAnonKey = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY;
                    if (!supabaseUrl || !supabaseAnonKey) return;
                    const transactionId = `txn_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
                    const response = await fetch(`${supabaseUrl}/functions/v1/dcb-init`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${supabaseAnonKey}`,
                        'Apikey': supabaseAnonKey,
                      },
                      body: JSON.stringify({
                        callback_ok: `${origin}/dcb-callback?dcb_ok=true`,
                        callback_ko: `${origin}/dcb-callback?dcb_cancel=1`,
                        transaction_id: transactionId,
                        smartuser_user_id: authState.user?.smartuserId,
                        campaign_countries_languages_id: campaignCountriesLanguagesId,
                      }),
                    });
                    if (!response.ok) return;
                    const data = await response.json();
                    if (data.url_redirect) {
                      const newTab = window.open(data.url_redirect, '_blank');
                      if (!newTab) window.location.href = data.url_redirect;
                    }
                  } catch (_) {}
                } else if (method === 'revenuecat_web') {
                  const baseUrl = REVENUECAT_CONFIG.web.purchaseLink;
                  const userId = authState.user?.smartuserId;
                  if (!baseUrl || !userId) {
                    Alert.alert(t('error'), t('paymentUnavailable'));
                    return;
                  }
                  if (Platform.OS === 'web' && typeof window !== 'undefined') {
                    const tab = window.open(`${baseUrl}/${userId}`, '_blank');
                    if (!tab) window.location.href = `${baseUrl}/${userId}`;
                  }
                } else if (method === 'revenuecat_app') {
                  const selectedProduct = productId
                    ? products.find(p => p.productId === productId)
                    : products[0];
                  if (selectedProduct) {
                    trackPaywallProductClick({
                      productId: selectedProduct.productId,
                      productTitle: selectedProduct.title,
                      localizedPrice: selectedProduct.localizedPrice,
                    });
                  }
                  try {
                    await purchaseSubscription(selectedProduct?.productId);
                    if (onPurchaseSuccess) {
                      onPurchaseSuccess();
                    } else {
                      trackPaywallClose(source, 'purchase_complete');
                      onClose();
                    }
                  } catch (error: any) {
                    if (error?.userCancelled) {
                      return;
                    }
                    console.error('[CustomPaywall] Native purchase error:', error);
                    Alert.alert(t('error'), error?.message || t('purchaseFailed'));
                  }
                }
              }}
              campaignCountriesLanguagesId={campaignCountriesLanguagesId}
              products={products}
              isPurchasing={isPurchasing}
              isIAPReady={isIAPReady}
            />

            {/* Learn More Button */}
            <TouchableOpacity
              style={[styles.learnMoreButton, { borderColor: primaryColor }]}
              onPress={handleLearnMore}
            >
              <Info size={18} color={primaryColor} />
              <Text style={[styles.learnMoreText, { color: primaryColor }]}>{t('learnMoreAboutSubscription')}</Text>
            </TouchableOpacity>

            {/* Restore Purchases Button */}
            <TouchableOpacity
              style={[styles.restoreButton, isPurchasing && styles.buttonDisabled]}
              onPress={handleRestore}
              disabled={isPurchasing || !isIAPReady}
            >
              <RotateCcw size={18} color="#888" />
              <Text style={styles.restoreButtonText}>
                {isPurchasing ? t('purchaseProcessing') : t('restorePurchases')}
              </Text>
            </TouchableOpacity>

            {/* Comprehensive Terms and Disclosures */}
            <View style={styles.termsContainer}>
              <Text style={styles.termsText}>
                {t('subscriptionTerms')}
              </Text>
              <View style={styles.termsLinks}>
                <TouchableOpacity onPress={handleOpenPricingConditions}>
                  <Text style={[styles.termsLink, { color: primaryColor }]}>{t('pricingConditions')}</Text>
                </TouchableOpacity>
                <Text style={styles.termsSeparator}>•</Text>
                <TouchableOpacity onPress={handleOpenTerms}>
                  <Text style={[styles.termsLink, { color: primaryColor }]}>{t('termsConditions')}</Text>
                </TouchableOpacity>
                <Text style={styles.termsSeparator}>•</Text>
                <TouchableOpacity onPress={handleOpenPrivacy}>
                  <Text style={[styles.termsLink, { color: primaryColor }]}>{t('privacyPolicy')}</Text>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </View>
      </View>

      {/* Subscription Details Modal */}
      <Modal
        visible={showDetailsModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowDetailsModal(false)}
      >
        <View style={styles.detailsOverlay}>
          <View style={styles.detailsContainer}>
            <View style={styles.detailsHeader}>
              <Text style={styles.detailsTitle}>{t('subscriptionDetails')}</Text>
              <TouchableOpacity onPress={() => setShowDetailsModal(false)} style={styles.detailsCloseButton}>
                <X size={22} color="#ffffff" />
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.detailsContent} showsVerticalScrollIndicator={false}>
              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>{t('whatIsIncluded')}</Text>
                <Text style={styles.detailsText}>{t('subscriptionDetailsDescription')}</Text>
              </View>

              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>{t('howBillingWorks')}</Text>
                <Text style={styles.detailsText}>{t('billingDescription')}</Text>
              </View>

              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>{t('howToCancel')}</Text>
                <Text style={styles.detailsText}>
                  {Platform.OS === 'ios'
                    ? t('cancellationDescriptionIos')
                    : Platform.OS === 'android'
                    ? t('cancellationDescriptionAndroid')
                    : t('cancellationDescription')}
                </Text>
              </View>

              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>{t('subscriptionExpiry')}</Text>
                <Text style={styles.detailsText}>{t('expiryDescription')}</Text>
              </View>

              <View style={styles.detailsSection}>
                <Text style={styles.detailsSectionTitle}>{t('deviceCompatibility')}</Text>
                <Text style={styles.detailsText}>{t('compatibilityDescription')}</Text>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>

    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: palette.black,
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: palette.neutral[700],
    borderTopLeftRadius: spacing.xl,
    borderTopRightRadius: spacing.xl,
    height: '90%',
    maxHeight: '90%',
    maxWidth: 600,
    width: '100%',
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 22,
    paddingBottom: 18,
  },
  closeButton: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
    backgroundColor: palette.neutral[600],
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    paddingHorizontal: 22,
  },
  scrollViewContent: {
    paddingBottom: 80,
  },
  learnMoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderRadius: radius.lg,
    padding: 14,
    marginBottom: 14,
    gap: 10,
  },
  learnMoreText: {
    fontSize: 15,
    fontWeight: typography.weight.medium,
  },
  restoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: palette.neutral[450],
    borderRadius: radius.lg,
    padding: 14,
    marginBottom: 18,
    gap: 10,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  restoreButtonText: {
    color: palette.neutral[350],
    fontSize: 15,
    fontWeight: typography.weight.medium,
  },
  termsContainer: {
    marginBottom: 26,
  },
  termsText: {
    color: palette.neutral[450],
    fontSize: 11,
    textAlign: 'center',
    lineHeight: 16,
    marginBottom: 14,
  },
  termsLinks: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  termsLink: {
    fontSize: 11,
    textDecorationLine: 'underline',
  },
  termsSeparator: {
    color: palette.neutral[450],
    fontSize: typography.fontSize.sm,
    marginHorizontal: spacing.xxs,
  },
  detailsOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 22,
  },
  detailsContainer: {
    backgroundColor: palette.neutral[700],
    borderRadius: spacing.lg,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
  },
  detailsHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 22,
    borderBottomWidth: 1,
    borderBottomColor: palette.neutral[650],
  },
  detailsTitle: {
    color: palette.white,
    fontSize: 22,
    fontWeight: 'bold',
  },
  detailsCloseButton: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
    backgroundColor: palette.neutral[600],
    justifyContent: 'center',
    alignItems: 'center',
  },
  detailsContent: {
    padding: 22,
  },
  detailsSection: {
    marginBottom: 26,
  },
  detailsSectionTitle: {
    color: palette.white,
    fontSize: 17,
    fontWeight: typography.weight.semibold,
    marginBottom: 10,
  },
  detailsText: {
    color: '#ccc',
    fontSize: 15,
    lineHeight: 24,
  },
  contextBanner: {
    marginHorizontal: 22,
    marginBottom: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radius.lg,
  },
  contextBannerText: {
    color: palette.white,
    fontSize: typography.fontSize.md,
    fontWeight: typography.weight.semibold,
    textAlign: 'center',
  },
});